import { useState } from "react";
import { ArrowRight, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { normaliseUKMobile, getPhoneError, getEmailError } from "@/lib/phone";
import { getAttribution } from "@/lib/attribution";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogClose,
} from "@/components/ui/dialog";

export type CalcContext = {
  crewSize: number;
  nights: number;
  nightlyRate: number;
  roomType: "single" | "twin";
  estimatedSaving: number;
  percentSaved: number;
};

interface FieldsProps {
  calcContext?: CalcContext;
  sourceVariant?: string;
  initialTeamSize?: string;
  /** Called right before the navigate('/thank-you') fires. Useful for closing a wrapping dialog. */
  onSubmitted?: () => void;
  /** If true, the internal heading/badge/blurb at the top of the form is hidden (e.g. when CalcPopup provides its own header). */
  hideHeader?: boolean;
}

/**
 * The actual lead-capture form (fields + submit handler). Exported so other flows
 * (e.g. CalcPopup) can render exactly the same form without duplicating logic.
 */
export const QuoteFormFields = ({
  calcContext,
  sourceVariant,
  initialTeamSize,
  onSubmitted,
  hideHeader,
}: FieldsProps) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    phone: "",
    email: "",
    teamSize: initialTeamSize || "",
    location: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateField = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (submitted) {
      if (field === "phone") {
        const required = !value.trim() ? "Enter your phone number" : "";
        setErrors((prev) => ({ ...prev, phone: required || getPhoneError(value) || "" }));
      } else if (field === "email") {
        const required = !value.trim() ? "Enter your email address" : "";
        setErrors((prev) => ({ ...prev, email: required || getEmailError(value) || "" }));
      } else {
        setErrors((prev) => ({ ...prev, [field]: !value.trim() ? "This field is required" : "" }));
      }
    }
  };

  const handleBlur = (field: string, value: string) => {
    if (field === "phone") {
      setErrors((prev) => ({ ...prev, phone: getPhoneError(value) || "" }));
    } else if (field === "email") {
      setErrors((prev) => ({ ...prev, email: getEmailError(value) || "" }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    const newErrors: Record<string, string> = {};
    Object.entries(formData).forEach(([key, val]) => { if (!val.trim()) newErrors[key] = "This field is required"; });
    const normalisedPhone = normaliseUKMobile(formData.phone);
    const phoneErr = getPhoneError(formData.phone);
    if (phoneErr) newErrors.phone = phoneErr;
    const emailErr = getEmailError(formData.email);
    if (emailErr) newErrors.email = emailErr;
    setErrors(newErrors);
    if (Object.values(newErrors).some(Boolean)) return;

    setIsSubmitting(true);
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data, error } = await supabase.functions.invoke("submit-lead", {
        body: {
          ...formData,
          phone: normalisedPhone,
          pageUrl: window.location.href,
          referrer: document.referrer || null,
          attribution: getAttribution(),
          sourceVariant,
          calcContext,
        },
      });
      if (error) console.error("Lead submission error:", error);
      else console.log("Lead submitted:", data);

      if (typeof window !== "undefined") {
        // @ts-expect-error dataLayer injected by GTM
        window.dataLayer = window.dataLayer || [];
        // @ts-expect-error see above
        window.dataLayer.push({
          event: "lead_submitted",
          variant: sourceVariant || "lp",
          ...(calcContext || {}),
        });
      }
    } catch (err) {
      console.error("Failed to submit lead:", err);
    }
    onSubmitted?.();
    navigate("/thank-you");
  };

  const inputClass = (field: string) =>
    `ios-safe-input px-3 py-[9px] border-[1.5px] rounded-md font-sans text-foreground outline-none bg-white focus:border-accent transition-colors placeholder:text-[#c4c9d4] ${errors[field] ? "border-destructive" : "border-border"}`;

  return (
    <form onSubmit={handleSubmit} className="p-[29px_25px_25px] flex flex-col gap-[3px]">
      {!hideHeader && (
        <>
          <div className="text-[16px] font-bold text-foreground">Get My Free Shortlist</div>
          <div className="inline-flex items-center gap-[5px] self-start mt-[2px] bg-secondary border border-[rgba(252,163,17,0.2)] rounded-full px-[10px] py-[4px]">
            <span className="w-2 h-2 bg-accent rounded-full flex-shrink-0" />
            <span className="text-[11.5px] font-semibold text-accent-dark">From £25 per person/night. Cheaper than hotels</span>
          </div>
          {calcContext && calcContext.estimatedSaving > 0 && (
            <p className="text-[12px] text-accent-dark font-semibold mt-2">
              Based on your inputs, you could save around <strong>£{Math.round(calcContext.estimatedSaving).toLocaleString("en-GB")}</strong> vs hotels.
            </p>
          )}
          <p className="text-[12.3px] text-muted-foreground leading-[1.5] my-[4px_0_12px] mt-1 mb-3">
            No obligation · No sourcing fees · Same-day shortlist
          </p>
        </>
      )}

      <div className="flex flex-col gap-1 mb-[9px]">
        <label className="text-[10.6px] font-bold text-foreground/65 uppercase tracking-[0.08em]">Your Name <span className="text-destructive">*</span></label>
        <input required value={formData.name} onChange={(e) => updateField("name", e.target.value)} className={inputClass("name")} placeholder="Your Name" />
        {errors.name && <span className="text-[10px] text-destructive font-medium mt-[2px]">{errors.name}</span>}
      </div>
      <div className="flex flex-col gap-1 mb-[9px]">
        <label className="text-[10.6px] font-bold text-foreground/65 uppercase tracking-[0.08em]">Company <span className="text-destructive">*</span></label>
        <input required value={formData.company} onChange={(e) => updateField("company", e.target.value)} className={inputClass("company")} placeholder="Company" />
        {errors.company && <span className="text-[10px] text-destructive font-medium mt-[2px]">{errors.company}</span>}
      </div>
      <div className="grid grid-cols-2 gap-[9px]">
        <div className="flex flex-col gap-1 mb-[9px]">
          <label className="text-[10.6px] font-bold text-foreground/65 uppercase tracking-[0.08em]">Phone <span className="text-destructive">*</span></label>
          <input required value={formData.phone} onChange={(e) => updateField("phone", e.target.value)} onBlur={(e) => handleBlur("phone", e.target.value)} inputMode="tel" autoComplete="tel" className={inputClass("phone")} placeholder="07XXX XXXXXX" />
          {errors.phone && <span className="text-[10px] text-destructive font-medium mt-[2px]">{errors.phone}</span>}
        </div>
        <div className="flex flex-col gap-1 mb-[9px]">
          <label className="text-[10.6px] font-bold text-foreground/65 uppercase tracking-[0.08em]">Email <span className="text-destructive">*</span></label>
          <input required type="email" value={formData.email} onChange={(e) => updateField("email", e.target.value)} onBlur={(e) => handleBlur("email", e.target.value)} className={inputClass("email")} placeholder="Email@co.com" />
          {errors.email && <span className="text-[10px] text-destructive font-medium mt-[2px]">{errors.email}</span>}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-[9px]">
        <div className="flex flex-col gap-1 mb-[9px]">
          <label className="text-[10.6px] font-bold text-foreground/65 uppercase tracking-[0.08em]">Team Size <span className="text-destructive">*</span></label>
          <select required aria-label="Team Size" value={formData.teamSize} onChange={(e) => updateField("teamSize", e.target.value)} className={`ios-safe-input px-3 py-[9px] border-[1.5px] rounded-md font-sans text-foreground outline-none bg-white focus:border-accent transition-colors appearance-none ${errors.teamSize ? "border-destructive" : "border-border"}`}>
            <option value="">Select…</option>
            <option>2–5</option>
            <option>6–15</option>
            <option>16–30</option>
            <option>31–50</option>
            <option>50+</option>
          </select>
          {errors.teamSize && <span className="text-[10px] text-destructive font-medium mt-[2px]">{errors.teamSize}</span>}
        </div>
        <div className="flex flex-col gap-1 mb-[9px]">
          <label className="text-[10.6px] font-bold text-foreground/65 uppercase tracking-[0.08em]">Project Location <span className="text-destructive">*</span></label>
          <input required value={formData.location} onChange={(e) => updateField("location", e.target.value)} className={inputClass("location")} placeholder="Location" />
          {errors.location && <span className="text-[10px] text-destructive font-medium mt-[2px]">{errors.location}</span>}
        </div>
      </div>
      <button type="submit" disabled={isSubmitting} className="w-full py-3 bg-accent hover:bg-[#fca311] text-foreground font-bold text-[13.5px] border-none rounded-md cursor-pointer mt-1 transition-all hover:-translate-y-px flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed">
        {isSubmitting ? "Submitting…" : "Get My Free Shortlist"} {!isSubmitting && <ArrowRight className="w-4 h-4" />}
      </button>
      <p className="text-center mt-[10px] text-[9.5px] font-bold text-[#6b7280] tracking-[0.07em] uppercase">
        We never share your info with anyone
      </p>
    </form>
  );
};

interface Props {
  children: React.ReactNode;
  calcContext?: CalcContext;
  sourceVariant?: string;
  initialTeamSize?: string;
  onOpen?: () => void;
}

const QuoteFormPopup = ({ children, calcContext, sourceVariant, initialTeamSize, onOpen }: Props) => {
  return (
    <Dialog onOpenChange={(open) => { if (open) onOpen?.(); }}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent
        className="p-0 border-0 max-w-lg w-[95vw] sm:w-full max-h-[90vh] bg-background overflow-hidden [&>button]:hidden"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogClose asChild>
          <button
            aria-label="Close"
            className="absolute top-3 right-3 z-10 rounded-full bg-muted/80 hover:bg-muted p-1.5 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </DialogClose>

        <div className="overflow-y-auto max-h-[90vh]">
          <div className="h-[3px] bg-gradient-to-r from-accent to-accent-dark" />
          <QuoteFormFields
            calcContext={calcContext}
            sourceVariant={sourceVariant}
            initialTeamSize={initialTeamSize}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteFormPopup;
